import { useState, FormEvent } from 'react';

const FORM_URL = '/api/contact';

type SubmitStatus = 'idle' | 'loading' | 'success' | 'error';

export default function ContactSection() {
  const [status, setStatus] = useState<SubmitStatus>('idle');
  const [charCount, setCharCount] = useState(0);

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (status === 'loading') return;

    const form = e.currentTarget;
    const formData = new FormData(form);

    const message = formData.get('message') as string;
    if (message && message.length > 500) {
      alert('Mesaj 500 karakteri geçemez.');
      return;
    }

    const body = new URLSearchParams();
    formData.forEach((value, key) => body.append(key, value as string));

    setStatus('loading');
    try {
      const res = await fetch(FORM_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: body.toString(),
      });
      if (res.ok) {
        setStatus('success');
        form.reset();
        setCharCount(0);
      } else {
        setStatus('error');
      }
    } catch {
      setStatus('error');
    }
  };

  return (
    <section id="contact" className="py-20 md:py-28 bg-white">
      <div className="max-w-7xl mx-auto px-4 md:px-6">
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-12">
          {/* Left Info */}
          <div className="lg:col-span-2">
            <p className="text-brand-orange font-semibold text-sm uppercase tracking-widest mb-4">İletişim</p>
            <h2 className="text-brand-navy font-heading font-bold text-3xl md:text-4xl mb-6 leading-tight">
              Bize
              <br />
              Ulaşın
            </h2>
            <p className="text-gray-500 mb-10 leading-relaxed">
              Fiyat teklifi, bilgi almak veya herhangi bir konuda yardım için bizimle iletişime geçin. En kısa sürede yanıt vereceğiz.
            </p>

            {/* Contact Items */}
            <div className="space-y-5">
              <a
                href="tel:+905322376576"
                className="group flex items-center gap-4 cursor-pointer"
              >
                <div className="w-12 h-12 flex items-center justify-center rounded-2xl bg-brand-orange/10 text-brand-orange group-hover:bg-brand-orange group-hover:text-white transition-all flex-shrink-0">
                  <i className="ri-phone-line text-xl"></i>
                </div>
                <div>
                  <div className="text-xs text-gray-400 mb-0.5">🇹🇷 Ana Hat</div>
                  <div className="text-brand-navy font-semibold">+90 532 237 65 76</div>
                </div>
              </a>

              <a
                href="tel:+491775958762"
                className="group flex items-center gap-4 cursor-pointer"
              >
                <div className="w-12 h-12 flex items-center justify-center rounded-2xl bg-brand-navy/5 text-brand-navy group-hover:bg-brand-navy group-hover:text-white transition-all flex-shrink-0">
                  <i className="ri-phone-line text-xl"></i>
                </div>
                <div>
                  <div className="text-xs text-gray-400 mb-0.5">🇩🇪 Almanya Hat 1</div>
                  <div className="text-brand-navy font-semibold">+49 177 595 87 62</div>
                </div>
              </a>

              <a
                href="tel:+491622437627"
                className="group flex items-center gap-4 cursor-pointer"
              >
                <div className="w-12 h-12 flex items-center justify-center rounded-2xl bg-brand-navy/5 text-brand-navy group-hover:bg-brand-navy group-hover:text-white transition-all flex-shrink-0">
                  <i className="ri-phone-line text-xl"></i>
                </div>
                <div>
                  <div className="text-xs text-gray-400 mb-0.5">🇩🇪 Almanya Hat 2</div>
                  <div className="text-brand-navy font-semibold">+49 162 243 76 27</div>
                </div>
              </a>

              <div className="flex items-start gap-4">
                <div className="w-12 h-12 flex items-center justify-center rounded-2xl bg-brand-orange/10 text-brand-orange flex-shrink-0">
                  <i className="ri-mail-line text-xl"></i>
                </div>
                <div>
                  <div className="text-xs text-gray-400 mb-1">E-Posta</div>
                  <a href="mailto:Mustafa@tolelojistik.com.tr" className="block text-brand-navy font-semibold text-sm hover:text-brand-orange transition-colors">
                    Mustafa@tolelojistik.com.tr
                  </a>
                  <a href="mailto:serkan@tolelojistik.com.tr" className="block text-brand-navy font-semibold text-sm hover:text-brand-orange transition-colors">
                    serkan@tolelojistik.com.tr
                  </a>
                </div>
              </div>
            </div>

            {/* Social Links */}
            <div className="mt-10 flex items-center gap-3">
              <a
                href="https://wa.me/905322376576"
                target="_blank"
                rel="nofollow noreferrer"
                className="w-11 h-11 flex items-center justify-center rounded-xl bg-whatsapp text-white hover:bg-whatsapp-dark transition-colors cursor-pointer"
              >
                <i className="ri-whatsapp-line text-xl"></i>
              </a>
              <a
                href="https://www.instagram.com/tolelojistik"
                target="_blank"
                rel="nofollow noreferrer"
                className="w-11 h-11 flex items-center justify-center rounded-xl text-white hover:opacity-90 transition-opacity cursor-pointer"
                style={{ background: 'linear-gradient(135deg, #f09433, #dc2743, #bc1888)' }}
              >
                <i className="ri-instagram-line text-xl"></i>
              </a>
              <a
                href="mailto:Mustafa@tolelojistik.com.tr"
                className="w-11 h-11 flex items-center justify-center rounded-xl bg-brand-orange text-white hover:bg-brand-orange-dark transition-colors cursor-pointer"
              >
                <i className="ri-mail-send-line text-xl"></i>
              </a>
            </div>
          </div>

          {/* Right Form */}
          <div className="lg:col-span-3">
            <div className="bg-brand-cream rounded-3xl p-6 md:p-10">
              <h3 className="text-brand-navy font-heading font-bold text-xl mb-6">Mesaj Gönderin</h3>

              {status === 'success' ? (
                <div className="flex flex-col items-center justify-center py-16 text-center">
                  <div className="w-16 h-16 flex items-center justify-center rounded-full bg-whatsapp/10 text-whatsapp mb-4">
                    <i className="ri-check-line text-3xl"></i>
                  </div>
                  <h4 className="text-brand-navy font-bold text-xl mb-2">Mesajınız İletildi!</h4>
                  <p className="text-gray-500">En kısa sürede size geri dönüş yapacağız.</p>
                  <button
                    onClick={() => setStatus('idle')}
                    className="mt-6 px-6 py-2.5 rounded-full border border-brand-orange text-brand-orange text-sm font-semibold hover:bg-brand-orange hover:text-white transition-colors cursor-pointer"
                  >
                    Yeni Mesaj
                  </button>
                </div>
              ) : (
                <form
                  data-readdy-form
                  id="tole-iletisim-form"
                  onSubmit={handleSubmit}
                  className="space-y-4"
                >
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm text-gray-500 mb-1.5 font-medium">Ad Soyad *</label>
                      <input
                        type="text"
                        name="name"
                        required
                        placeholder="Adınız Soyadınız"
                        className="w-full bg-white border border-gray-200 rounded-xl px-4 py-3 text-sm text-brand-navy placeholder-gray-400 focus:outline-none focus:border-brand-orange transition-colors"
                      />
                    </div>
                    <div>
                      <label className="block text-sm text-gray-500 mb-1.5 font-medium">Telefon</label>
                      <input
                        type="tel"
                        name="phone"
                        placeholder="+90 5XX XXX XX XX"
                        className="w-full bg-white border border-gray-200 rounded-xl px-4 py-3 text-sm text-brand-navy placeholder-gray-400 focus:outline-none focus:border-brand-orange transition-colors"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm text-gray-500 mb-1.5 font-medium">E-Posta *</label>
                    <input
                      type="email"
                      name="email"
                      required
                      placeholder="ornek@email.com"
                      className="w-full bg-white border border-gray-200 rounded-xl px-4 py-3 text-sm text-brand-navy placeholder-gray-400 focus:outline-none focus:border-brand-orange transition-colors"
                    />
                  </div>

                  <div>
                    <label className="block text-sm text-gray-500 mb-1.5 font-medium">Hizmet Türü</label>
                    <select
                      name="service_type"
                      className="w-full bg-white border border-gray-200 rounded-xl px-4 py-3 text-sm text-brand-navy focus:outline-none focus:border-brand-orange transition-colors cursor-pointer"
                    >
                      <option value="">Seçiniz...</option>
                      <option value="Parsiyel Taşımacılık">Parsiyel Taşımacılık</option>
                      <option value="Komple Araç">Komple Araç</option>
                      <option value="Ev & Ofis Taşıma">Ev &amp; Ofis Taşıma</option>
                      <option value="Hızlı Kargo">Hızlı Kargo</option>
                      <option value="Diğer">Diğer</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm text-gray-500 mb-1.5 font-medium">Mesajınız *</label>
                    <textarea
                      name="message"
                      required
                      rows={4}
                      maxLength={500}
                      placeholder="Taşımak istediğiniz yük, güzergah veya diğer bilgilerinizi paylaşın..."
                      onChange={(e) => setCharCount(e.target.value.length)}
                      className="w-full bg-white border border-gray-200 rounded-xl px-4 py-3 text-sm text-brand-navy placeholder-gray-400 focus:outline-none focus:border-brand-orange transition-colors resize-none"
                    ></textarea>
                    <div className={`text-right text-xs mt-1 ${charCount > 450 ? 'text-brand-orange' : 'text-gray-400'}`}>
                      {charCount}/500
                    </div>
                  </div>

                  {status === 'error' && (
                    <div className="flex items-center gap-2 text-red-500 text-sm bg-red-50 rounded-xl p-3">
                      <span className="w-4 h-4 flex items-center justify-center">
                        <i className="ri-error-warning-line"></i>
                      </span>
                      Bir hata oluştu. Lütfen tekrar deneyin.
                    </div>
                  )}

                  <button
                    type="submit"
                    disabled={status === 'loading'}
                    className="w-full flex items-center justify-center gap-2 bg-brand-orange hover:bg-brand-orange-dark text-white py-4 rounded-xl font-semibold transition-all hover:scale-[1.02] disabled:opacity-60 disabled:cursor-not-allowed cursor-pointer"
                  >
                    {status === 'loading' ? (
                      <>
                        <i className="ri-loader-4-line animate-spin text-lg"></i>
                        Gönderiliyor...
                      </>
                    ) : (
                      <>
                        Gönder
                        <span className="w-5 h-5 flex items-center justify-center">
                          <i className="ri-arrow-right-line text-lg"></i>
                        </span>
                      </>
                    )}
                  </button>
                </form>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
